'use client';

import { Box, Button, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { domAnimation, LazyMotion, m, useScroll, useTransform } from 'motion/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { Link } from '@/configurations/i18n/navigation';

import { FOOTER_BG, FOOTER_HEIGHT_VAR } from './footer-constants';

const FOOTER_LINKS = [
    { key: 'privacy', href: '/privacy-policy' },
    { key: 'terms', href: '/terms-and-conditions-of-sale' },
    { key: 'contact', href: '/contact' },
] as const;

/**
 * Footer global fixé en bas de l'écran, caché derrière le contenu (z-index 0).
 * Le `<main>` du layout réserve un espace de révélation égal à la hauteur du
 * footer : en fin de scroll, le contenu (fond plein, coins bas arrondis) glisse
 * au-dessus du footer qui se dévoile. La hauteur réelle est exposée en variable
 * CSS `--footer-height` pour rester juste en responsive.
 */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeToReducedMotion(onChange: () => void): () => void {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
}

// Hydrates with the server answer (motion allowed) and only then reads the media query, unlike
// motion's useReducedMotion which differs between the server and the first client render.
function usePrefersReducedMotion(): boolean {
    return useSyncExternalStore(
        subscribeToReducedMotion,
        () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
        () => false,
    );
}

export function Footer() {
    const t = useTranslations('footer');
    const footerRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const element = footerRef.current;
        if (!element) return undefined;

        const root = document.documentElement;
        const sync = () => root.style.setProperty(FOOTER_HEIGHT_VAR, `${element.offsetHeight}px`);
        sync();

        const observer = new ResizeObserver(sync);
        observer.observe(element);
        return () => {
            observer.disconnect();
            root.style.removeProperty(FOOTER_HEIGHT_VAR);
        };
    }, []);

    // Entrée douce du contenu pendant les derniers % de scroll (au moment où le
    // footer se dévoile). Purement décoratif : la révélation elle-même est CSS.
    const { scrollYProgress } = useScroll();
    const opacity = useTransform(scrollYProgress, [0.85, 1], [0, 1]);
    const y = useTransform(scrollYProgress, [0.85, 1], [24, 0]);
    const prefersReducedMotion = usePrefersReducedMotion();

    return (
        <Box
            component="footer"
            ref={footerRef}
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 0,
                bgcolor: FOOTER_BG,
                px: 3,
                py: 5,
            }}
        >
            <LazyMotion features={domAnimation} strict>
                <m.div style={{ opacity, y: prefersReducedMotion ? 0 : y }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={{ xs: 3, md: 2 }}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            maxWidth: 1200,
                            mx: 'auto',
                            width: '100%',
                        }}
                    >
                        <Box
                            component={Link}
                            href="/"
                            aria-label="Odys"
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Image
                                src="/logo-footer.svg"
                                alt="Odys"
                                width={103}
                                height={32}
                                style={{ display: 'block', height: 32, width: 'auto' }}
                            />
                        </Box>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={{ xs: 1, sm: 2 }}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            {FOOTER_LINKS.map(({ key, href }) => (
                                <Button
                                    key={key}
                                    component={Link}
                                    href={href}
                                    variant="text"
                                    sx={{
                                        color: 'common.white',
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                            backgroundColor: (theme) =>
                                                alpha(theme.palette.common.white, 0.08),
                                        },
                                    }}
                                >
                                    {t(key)}
                                </Button>
                            ))}
                        </Stack>
                    </Stack>
                </m.div>
            </LazyMotion>
        </Box>
    );
}
